import React, { useMemo } from 'react'
import { useState, useEffect, useContext } from 'react'
import PropTypes, { number, string } from 'prop-types'
import { Checkbox, FormControlLabel, Box, Radio, CardMedia } from '@mui/material'
import { isNumber } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { convertPrice, isElectronicMenu, isValidArray } from '~/utils'
import { ThemeContext } from '~/theme/ThemeProvider'
import { THEMES_NAME } from '~/theme/constants'
import {
  changeToppingCheckbox,
  changeToppingRadio,
  selectFoodOptionTypes,
} from '../slices/itemDetailSlice'
import { calcQuantityFoodOptionType, checkErrorQuantityTopping } from '~/utils/foodDetail'
import { SUKIYA_TOPPING_TYPE } from '~/constants/topping'
import useIsTablet from '~/hooks/useIsTablet'
import styles from '../styles/ToppingType.module.scss'
import classNames from 'classnames/bind'
import ControlToppingImage from './ControlToppingImage'
const cx = classNames.bind(styles)

// Hiển thị topping có ảnh
const ToppingImage = ({
  id,
  name,
  min,
  max,
  required,
  food_options,
  auto_add,
  food_option_id_related,
  sukiya_topping_type,
  selected_default,
  isLastItem,
}) => {
  const foodOptionID = food_options[0].id
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { currentThemeObject } = useContext(ThemeContext)
  const { colorText12, colorText1 } = currentThemeObject

  const foodOptionTypesSelected = useSelector(selectFoodOptionTypes)
  const [idRadio, setIdRadio] = useState(null) // Topping đã được chọn (RadioBox)
  const [idsCheckBox, setIdsCheckBox] = useState([]) // mảng các id food option (ChexBox)
  const isTablet = useIsTablet()

  // Topping type có giới hạn số lượng hay không
  const isToppingTypeHasLimitQuantity = isNumber(min) && isNumber(max)

  const isRequiredDefaultValueFoodOption =
    required && food_options.length > 0 && !isToppingTypeHasLimitQuantity && !auto_add

  const isToppingSetSukiya = sukiya_topping_type && sukiya_topping_type === SUKIYA_TOPPING_TYPE.Set

  // Topping type có phải là kiểu radio không. Trường hợp required đối với nhà hàng thường, hoặc type là set đối với sukiya
  const isRadioType = isRequiredDefaultValueFoodOption || isToppingSetSukiya

  // Biến dùng để check xem có lỗi topping hay không
  const isErrorToppingType = useMemo(() => {
    const totalQuantityFoodOptionType = calcQuantityFoodOptionType(foodOptionTypesSelected, id)

    const error = checkErrorQuantityTopping({ required, min, max, totalQuantityFoodOptionType })
    let errorText = ''
    if (error.isErrorRequired) errorText = t('label.required')
    if (error.isErrorMin) errorText = t('errors.warningMin', { min: min })
    if (error.isErrorMax) errorText = t('errors.warningMax', { max: max })

    return !!errorText
  }, [foodOptionTypesSelected, id, max, min, required, t])

  // Nếu topping type thuộc kiểu Radio thì mặc định chọn option đầu tiên
  // Và phải selected_default = true thì mới chọn item đầu
  useEffect(() => {
    if (isRequiredDefaultValueFoodOption && selected_default) {
      setIdRadio(foodOptionID)
    } else {
      setIdRadio(null)
    }

    // Trong trường hợp food_option_id_related cũng phải set lại giá trị default này
  }, [
    auto_add,
    foodOptionID,
    isRequiredDefaultValueFoodOption,
    food_option_id_related,
    selected_default,
  ])

  /** select toàn bộ food_options nếu topping_type là auto_add */
  useEffect(() => {
    if (auto_add & isValidArray(food_options)) {
      setIdsCheckBox(food_options.map((item) => item.id))
    }
  }, [auto_add, food_options])

  // Xử lý change input radio
  const handleChangeInputRadio = (idFoodOption) => {
    setIdRadio(idFoodOption)
    // Xử lý lưu trữ dữ liệu trên redux
    dispatch(changeToppingRadio({ foodOptionId: idFoodOption, foodOptionTypeId: id }))
  }

  /** Xử lý change checkbox input
   * nếu là auto_add thì ko thể bỏ chọn
   */
  const handleChangeCheckbox = (foodOptionId) => {
    if (auto_add) return
    const checked = idsCheckBox.includes(foodOptionId)

    setIdsCheckBox((prevIds) => {
      if (checked) return prevIds.filter((item) => item !== foodOptionId)

      return [...prevIds, foodOptionId]
    })
    // Xử lý lưu trữ dữ liệu trên redux
    dispatch(changeToppingCheckbox({ foodOptionId, foodOptionTypeId: id, checked }))
  }

  /** Kiểm tra food option có giới hạn số lượng hay không  */
  const hasLimitFoodOption = (topping) => {
    return isNumber(topping.min) && isNumber(topping.max)
  }

  /**
   * Kiểm tra checkbox có bị disabled khi đã thoả mãn điều kiện hay không
   * @param {number} foodOptionId
   * @returns {boolean}
   */
  const isCheckboxDisabled = (foodOptionId) => {
    const isSelected = showBtnQuantity(foodOptionId)

    return (
      isToppingTypeHasLimitQuantity &&
      calcQuantityFoodOptionType(foodOptionTypesSelected, id) >= max &&
      !isSelected
    )
  }

  const renderToppingTypeName = () => {
    return (
      <h4 className={cx('topping_type_name')} style={{ color: currentThemeObject.colorText1 }}>
        {name}
      </h4>
    )
  }

  const renderToppingTypeSubRow = () => {
    return (
      <div
        className={cx('topping_type_sub_row')}
        style={{
          color: isErrorToppingType ? colorText12 : colorText1,
        }}>
        {required ? <span>*{t('label.required')}</span> : <span>{t('label.optional')}</span>}
        {isToppingTypeHasLimitQuantity && <span>・</span>}
        {isToppingTypeHasLimitQuantity && (
          <span>{t('titles.topping_type_quantity', { min: min, max: max })}</span>
        )}
      </div>
    )
  }

  const renderInputRadio = (foodOption) => {
    const { id } = foodOption

    return (
      <Radio
        id={`${id}`}
        size="medium"
        sx={{
          fontSize: '3rem',
          width: '3rem',
          paddingBottom: '0px',
          paddingTop: '0px',
          color:
            currentThemeObject.value === THEMES_NAME.mango
              ? '#929292'
              : currentThemeObject.colorText1,
          '&.Mui-checked': {
            color:
              currentThemeObject.value === THEMES_NAME.mango
                ? '#AD45FF'
                : currentThemeObject.color5,
          },
          '& .MuiSvgIcon-fontSizeMedium': {
            fontSize: isTablet ? '3rem' : '2.4rem',
          },
        }}
        checked={idRadio === id}
        onChange={() => handleChangeInputRadio(id)}
      />
    )
  }

  const renderInputCheckBox = (foodOption) => {
    const { id } = foodOption

    return (
      <Checkbox
        id={`${id}`}
        size="medium"
        sx={{
          padding: 0,
          color:
            currentThemeObject.value === THEMES_NAME.mango
              ? '#929292'
              : currentThemeObject.colorText1,
          '&.Mui-checked': {
            color:
              currentThemeObject.value === THEMES_NAME.mango
                ? '#AD45FF'
                : currentThemeObject.color5,
          },
          '& .MuiSvgIcon-fontSizeMedium': {
            fontSize: isTablet ? '3rem' : '2.4rem',
          },
        }}
        checked={idsCheckBox.includes(id)}
        onChange={() => handleChangeCheckbox(id)}
        disabled={isCheckboxDisabled(id)}
      />
    )
  }

  // Render component của topping (Checkbox / Radio)
  const renderToppingSelectComponent = (foodOption) => {
    const { id: foodOptionId } = foodOption
    const selectComponent = isRadioType
      ? renderInputRadio(foodOption)
      : renderInputCheckBox(foodOption)

    return (
      <FormControlLabel
        className={cx('form_control_label')}
        key={foodOptionId}
        control={selectComponent}
      />
    )
  }

  // Chỉ hiển thị nút cộng trừ food option trong trường hợp được check
  const showBtnQuantity = (foodOptionId) => {
    return idRadio === foodOptionId || idsCheckBox.includes(foodOptionId)
  }

  /**
   * Render giá của topping (nếu có)
   * @param {object} topping
   * @returns {jsx}
   */
  const renderPrice = ({ price }) => {
    return (
      isNumber(price) && (
        <Box component="p" className={cx('price')} color="baseColor.colorText1">
          {convertPrice(price)}
        </Box>
      )
    )
  }

  // Hiển thị nút tăng giảm số lượng
  const renderQuantityController = (foodOption) => {
    const isShowAction = showBtnQuantity(foodOption.id) && hasLimitFoodOption(foodOption)

    if (!isShowAction) return null

    return (
      <ControlToppingImage
        {...foodOption}
        minFoodOptionType={min}
        maxFoodOptionType={max}
        required={required}
        idFoodOptionType={id}
        is_topping_image
      />
    )
  }

  // Hiển thị danh sách food options
  const renderFoodOptions = () => {
    return food_options.map((foodOption) => {
      return (
        <Box
          key={foodOption.id}
          component="label"
          htmlFor={foodOption.id}
          className={cx('food_option_item')}>
          <CardMedia component="img" image={foodOption.image} className={cx('image')} />
          <Box component="h3" className={cx('name')} color="baseColor.colorText1">
            {foodOption.name}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {renderPrice(foodOption)}
            {renderToppingSelectComponent(foodOption)}
          </Box>
          {!isElectronicMenu() && renderQuantityController(foodOption)}
        </Box>
      )
    })
  }

  return (
    <div className={cx('wrapper')}>
      <div className={cx('topping_name_wrap')}>
        {renderToppingTypeName()}
        {renderToppingTypeSubRow()}
      </div>
      <div className={cx('food_options_wrap')}>{renderFoodOptions()}</div>
      {!isLastItem && (
        <Box
          component="div"
          className={cx('divider')}
          height="1px"
          bgcolor="baseColor.colorText1"
        />
      )}
    </div>
  )
}

export default React.memo(ToppingImage)

ToppingImage.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  required: PropTypes.bool,
  food_options: PropTypes.array,
  auto_add: PropTypes.bool,
  food_option_id_related: PropTypes.oneOfType([string, number]),
  sukiya_topping_type: PropTypes.string,
  selected_default: PropTypes.bool,
  isLastItem: PropTypes.bool,
}
